import React from "react";
// ANT DESIGN COMPONENTS
import { Tooltip } from "antd";
// ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
// UTILS
import { unnamedFloorWarning } from "../../../../utils/floorButtonLabel";
// INTERFACES
import { BankFloorInterface } from "../../../../interfaces/Bank.interface";

/**
 * Says why a floor strip's buttons are showing numbers instead of names.
 *
 * Falling back is the right runtime behaviour — a button that renders nothing
 * because a floor was never named is worse than one showing its number, and an
 * operator mid-call cannot go and fix the bank record. But the fallback is
 * silent, and a silent fallback is indistinguishable from a bank that genuinely
 * calls its floors 1 through 12. This makes the difference visible without
 * making it obstructive.
 *
 * The bank's setup step is where it gets fixed, and unnamed floors are an error
 * there. This is the guard for banks that were already saved, or that were
 * created through the API rather than the wizard.
 *
 * Renders nothing when every floor is named, which is the case this component
 * is expected to be in.
 */
export default function UnnamedFloorWarning({
  floors,
}: {
  floors: ReadonlyArray<BankFloorInterface>;
}) {
  const warning = unnamedFloorWarning(floors);
  if (!warning) return null;

  return (
    <Tooltip title={warning}>
      {/* The role and label live on the span, not on the icon. FontAwesome
          renders its svg with `aria-hidden="true"` by default and keeps it,
          so an aria-label passed to the icon lands on an element already
          removed from the accessibility tree — the warning would be visible
          and, for a screen reader, absent.

          No tabIndex. The obvious move is to make the span focusable so the
          tooltip opens on keyboard focus, but a focus stop that exists only to
          reveal text already carried in the accessible name adds a tab stop
          per floor strip and reveals nothing new — which is what
          jsx-a11y/no-noninteractive-tabindex is objecting to, correctly. The
          aria-label IS the warning; the tooltip is the pointer user's copy. */}
      <span
        role="img"
        aria-label={warning}
        className="generalStyles__warning actionCard__floorWarning"
      >
        <FontAwesomeIcon icon={faTriangleExclamation} />
      </span>
    </Tooltip>
  );
}
